import { useFormContext } from "react-hook-form";

import { Input, Label } from "@fronterahealth/frontera-ui-components";

import { DynamicScoreTablesSection } from "@components/AssessmentFormRenderer/types/layoutTypes";

interface FormDynamicScoreTablesProps {
  section: DynamicScoreTablesSection;
}

/**
 * Renders an array of table objects, each with its own column headers and rows.
 * Each element is shaped like { "Table Name": string, "Column Headers": string[], "Rows": Record[] }.
 *
 * Uses watch/setValue instead of register to avoid the zod resolver's
 * internal regex matching on field paths — column headers can contain
 * regex-special characters like parentheses.
 */
export const FormDynamicScoreTables: React.FC<FormDynamicScoreTablesProps> = ({ section }) => {
  const { watch, setValue } = useFormContext();
  const tables: Record<string, unknown>[] = watch(section.source) ?? [];
  const editable = section.editable ?? true;

  return (
    <div className="flex flex-col gap-6">
      <Label displayType="loud">{section.label}</Label>
      {tables.map((table, tableIndex) => {
        const tableName = table[section.tableNameKey] as string | undefined;
        const headers = (table[section.headersKey] as string[]) ?? [];
        const rows = (table[section.rowsKey] as Record<string, string>[]) ?? [];

        if (!headers.length || !rows.length) return null;

        return (
          <div key={`${section.source}-${tableIndex}`} className="flex flex-col gap-2">
            {tableName && <p className="text-base font-semibold text-gray-800">{tableName}</p>}
            <div className="flex flex-row flex-wrap gap-x-4 border-b border-gray-200 pb-1">
              {headers.map((header, colIndex) => (
                <div key={header} className={colIndex === 0 ? "w-40 flex-auto" : "w-20 flex-auto"}>
                  <Label displayType="normal">{header}</Label>
                </div>
              ))}
            </div>
            {rows.map((row, rowIndex) => {
              const rowBasePath = `${section.source}.${tableIndex}.${section.rowsKey}.${rowIndex}`;
              return (
                <div key={rowBasePath} className="flex flex-row flex-wrap items-center gap-x-4">
                  {headers.map((header, colIndex) => {
                    const value = row[header];
                    const cellPath = `${rowBasePath}.${header}`;

                    if (!editable) {
                      return (
                        <div key={header} className={colIndex === 0 ? "w-40 flex-auto" : "w-20 flex-auto"}>
                          <Label>{value !== "" && value != null ? value : "—"}</Label>
                        </div>
                      );
                    }

                    return (
                      <div key={header} className={colIndex === 0 ? "w-40 flex-auto" : "w-20 flex-auto"}>
                        <Input
                          name={cellPath}
                          label=""
                          value={value ?? ""}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setValue(cellPath, e.target.value, { shouldDirty: true })
                          }
                          inputSize="full"
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};
